import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// API
import apibridge from '../../../apibridge';

// REDUX
import { useAppDispatch, useAppSelector } from '../../../store/slice';
import { UserSlice } from '../../../store/slice/User';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';

// PACKAGES
import { Container } from 'react-bootstrap';
import { Spinner } from 'react-bootstrap';

// UTILS
import { SITE_KEY, loadRecaptchaScript } from '../../../libs/utils';
import { guid } from '../../../libs/utils';

const StaffImpersonation: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const user = useAppSelector((state) => state.user);

	const [hasStartedImpersonation, setHasStartedImpersonation] = useState(false);

	// Get token from URL
	const searchParams = new URLSearchParams(window.location.search);

	const token = searchParams.get('token') || '';

	const doLogin = async () => {
		try {
			// Execute recaptcha
			const captchaToken = await window.grecaptcha.execute(SITE_KEY, { action: 'submit' });

			// Call API
			const response = await apibridge.postImpersonate({
				token,
				captchaToken
			});

			if (response.data) {
				if (response.data.isError) {
					if (response.data.validationErrors) {
						for (const err of response.data.validationErrors) {
							dispatch(
								ToastMessagesSlice.actions.add({
									id: guid(),
									type: 'danger',
									heading: 'Impersonation failed',
									description: err.reason || 'Unable to impersonate user. Please try again'
								})
							);
						}
					}
				} else if (response.data.result) {
					// Login successful
					const { refreshToken = '', token = '' } = response.data.result;

					localStorage.setItem('refreshToken', refreshToken);
					localStorage.setItem('token', token);

					// Update user state
					dispatch(
						UserSlice.actions.update({
							loggedIn: true
						})
					);

					navigate('/plans');
				}
			}
		} catch (error) {
			dispatch(
				ToastMessagesSlice.actions.add({
					id: guid(),
					type: 'danger',
					heading: 'Login failed',
					description: 'Failed to impersonate user. Please try again.'
				})
			);
		}
	};

	useEffect(() => {
		// Load recaptcha script
		loadRecaptchaScript();

		// If no token, navigate to login
		if (!token) {
			navigate('/login');
			return;
		}

		if (token && !hasStartedImpersonation) {
			setHasStartedImpersonation(true);

			// Log out user if logged in
			if (user.id) {
				dispatch(UserSlice.actions.logOut());
				localStorage.removeItem('token');
				localStorage.removeItem('refreshToken');
				sessionStorage.clear();
			}

			// Wait 3 seconds for recaptcha to load, then do login if token exists and not attempted
			setTimeout(() => {
				// Do login
				doLogin();
			}, 3000);
		}
	}, [token, user.id, hasStartedImpersonation, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<picture>
				<source srcSet="./img/login-bg.webp" />
				<img
					src="./img/login-bg.jpg"
					className="position-fixed w-100 h-100 object-fit object-fit-cover user-select-none"
					alt="Tiled books background"
				/>
			</picture>

			<main className="page-staff-login page-impersonation d-flex flex-column flex-grow-1">
				<Container className="position-relative z-1 d-flex flex-column flex-grow-1 align-items-center">
					<div className="d-flex flex-column justify-content-center flex-grow-1">
						<div className="form-rounded-corners my-4">
							<div className="d-flex align-items-center justify-content-center">
								<Spinner className="spinner-impersonation" />
							</div>

							<h2 className="text-shades-800">Impersonating user...</h2>
						</div>
					</div>
				</Container>
			</main>
		</>
	);
};

export default StaffImpersonation;
